import { useRef, useState } from "react"
import { toast } from "sonner"
import { FileText, Loader2, X } from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { FileCategory } from "@/modules/shared/hooks/use-file-validation"
import {
  DraggableFileUpload,
  DraggableFileUploadRef,
} from "@/modules/shared/ui/components/custom/file-upload"
import { useCandidateDocuments } from "../../hooks/useCandidateDocuments"

interface UploadDocumentDialogProps {
  candidateId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UploadDocumentDialog({
  candidateId,
  open,
  onOpenChange,
}: UploadDocumentDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState("")
  const fileUploadRef = useRef<DraggableFileUploadRef>(null)
  const { uploadDocument, isUploading } = useCandidateDocuments(candidateId)

  const handleFileSelected = (selected: File) => {
    setFile(selected)
    if (!fileName) {
      setFileName(selected.name)
    }
  }

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file before continuing.")
      return
    }
    try {
      await uploadDocument({ file, fileName: fileName || undefined })
      toast.success("Document uploaded successfully")
      handleClose()
    } catch {
      toast.error("Failed to upload document")
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      setFile(null)
      setFileName("")
      fileUploadRef.current?.clearError()
      onOpenChange(false)
    }
  }

  const getDisplayName = (f: File): string =>
    f.name.length > 30 ? f.name.substring(0, 27) + "..." : f.name

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] bg-card">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2">
            Upload a document for this candidate. Accepted formats: PDF, DOC,
            DOCX (Max 5MB)
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {!file ? (
            <DraggableFileUpload
              ref={fileUploadRef}
              fileCategory={FileCategory.Document}
              onFileSelected={handleFileSelected}
            />
          ) : (
            <div className="border border-border rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-blue-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {getDisplayName(file)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  setFile(null)
                  setFileName("")
                }}
                disabled={isUploading}
                className="text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="doc-name">File name override (optional)</Label>
            <Input
              id="doc-name"
              placeholder="Override file name"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isUploading || !file}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload Document"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
